import React, { useEffect, useRef, useState } from 'react';

import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import classes from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import { usePinboardWidgetWrapperContext } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';

interface Props {
    children: React.ReactNode | React.ReactNode[];
    titleComponents?: React.ReactNode | React.ReactNode[];
    titleFilters?: React.ReactNode | React.ReactNode[];
    title?: React.ReactNode;
    tooltip?: IToolTip;
    className?: string;
    id?: string;
}

export const WidgetWithTitle = ({
    children,
    titleComponents,
    titleFilters,
    title = '',
    tooltip,
    className,
    id,
}: Props) => {
    const [titleComponentsWithProps, setTitleComponentsWithProps] = useState<
        React.ReactNode | React.ReactNode[] | null
    >(null);
    const [isWidgetHovered, setIsWidgetHovered] = useState(false);
    const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const boundaryRef = useRef<HTMLDivElement>(null);
    const { isFullscreen } = usePinboardWidgetWrapperContext();

    useEffect(() => {
        if (boundaryRef?.current) {
            const clonedElements = React.Children.map(titleComponents, (child) => {
                if (React.isValidElement(child)) {
                    return child.type === ExportDropdown ? React.cloneElement(child, { ref: boundaryRef }) : child;
                }
                return child;
            });
            setTitleComponentsWithProps(clonedElements);
        }
    }, [boundaryRef, titleComponents]);

    const handleMouseEnter = () => {
        //set hover true and clear timeout when widget is hovered
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        setIsWidgetHovered(true);
    };
    const handleMouseLeave = () => {
        //set hover false on  mouse leave
        hoverTimeoutRef.current = setTimeout(() => {
            setIsWidgetHovered(false);
        }, 300);
    };
    useEffect(() => {
        // Cleanup timer
        return () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
            }
        };
    }, []);

    const titleFiltersArray = titleFilters ? React.Children.toArray(titleFilters) : [];

    return (
        <div
            data-testid={'widget-with-title'}
            className={`${classes.WidgetWithTitle} ${className || ''} ${isWidgetHovered ? classes.hovered : ''}`}
            ref={boundaryRef}
            id={id}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className={classes.titleArea}>
                <div data-testid={'title-components'} className={classes.titleComponents}>
                    <h3 id={id ? `${id}_title` : undefined}>{title}</h3>
                    {(tooltip || titleComponentsWithProps) && (
                        <div className={`${classes.endOfLine} ${isFullscreen ? classes.fullscreen : ''}`}>
                            {titleComponentsWithProps}
                            {tooltip && <TooltipIcon tooltip={tooltip} />}
                        </div>
                    )}
                </div>
                {titleFiltersArray.length > 0 && <div className={classes.titleFilters}>{titleFilters}</div>}
            </div>
            <div data-testid={'area-content'} className={classes.contentArea}>
                {children}
            </div>
        </div>
    );
};
